import {
    CButton,
    CCol,
    CModal,
    CModalBody,
    CModalHeader,
    CModalTitle,
    CContainer,
    CRow,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
    cilBell,
    cilCalendar,
    cilCloudDownload,
    cilColorPalette,
    cilEnvelopeClosed,
    cilExternalLink,
    cilHeart,
    cilInfo,
    cilLocationPin,
    cilMedicalCross,
    cilNotes,
    cilPaw,
    cilPeople,
    cilPhone,
    cilResizeBoth,
    cilStar,
    cilTag,
    cilUser,
} from "@coreui/icons";
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CustomBreadcrumbs, LoadingOverlay } from "../../components";
import { AppContext } from "../../context/AppContext";
import { FICHAS_URL, PUBLIC_URL } from "../../constants";
import styles from "./Pet.module.css";

const yesNo = (value) => (value === null || value === undefined ? "—" : value ? "Sí" : "No");

const Field = ({ icon, label, children }) => (
    <CCol xs={12} sm={6} lg={4}>
        <div className={styles.field}>
            <CIcon icon={icon} className={styles.fieldIcon} />
            <div>
                <div className={styles.fieldLabel}>{label}</div>
                <div className={styles.fieldValue}>{children || "—"}</div>
            </div>
        </div>
    </CCol>
);

const Section = ({ icon, title, children }) => (
    <div className={styles.card}>
        <div className={styles.cardHeader}>
            <span className={styles.cardIcon}>
                <CIcon icon={icon} />
            </span>
            {title}
        </div>
        <div className={styles.cardBody}>{children}</div>
    </div>
);

const Pet = () => {
    const { petId } = useParams();
    const { getPet, downloadPetImage } = useContext(AppContext);
    const [pet, setPet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewerImage, setViewerImage] = useState(null);

    const imageUrl = (img) => `${PUBLIC_URL}${img.path}`;

    // El servidor de imágenes no permite CORS: la descarga pasa por la API.
    const downloadImage = async (img) => {
        const res = await downloadPetImage(petId, img.id);
        if (!res) return;
        const blobUrl = URL.createObjectURL(res.data);
        const extension = (img.path.split(".").pop() || "jpg").split("?")[0];
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `Findpet - ${pet.pet_identification} - ${pet.name}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };

    useEffect(() => {
        setLoading(true);
        getPet(petId)
            .then((res) => setPet(res?.data?.data ?? res?.data ?? null))
            .finally(() => setLoading(false));
    }, [petId]);

    const mainImage = pet?.images?.[0];

    return (
        <div>
            <CustomBreadcrumbs
                parentTitle="Mascotas"
                parentUrl="/dashboard/pets"
                activeTitle={pet?.name || "Mascota"}
            />
            <LoadingOverlay isVisible={loading} />
            {pet && (
                <CContainer className="mt-4 mb-5">
                    <div className={styles.hero}>
                        {mainImage ? (
                            <img
                                src={imageUrl(mainImage)}
                                alt={pet.name}
                                className={`${styles.avatar} ${styles.zoomable}`}
                                onClick={() => setViewerImage(mainImage)}
                            />
                        ) : (
                            <div className={`${styles.avatar} ${styles.avatarEmpty}`}>
                                <CIcon icon={cilPaw} size="3xl" />
                            </div>
                        )}
                        <div className={styles.heroInfo}>
                            <h2 className={styles.name}>{pet.name}</h2>
                            <div className={styles.chips}>
                                <span className={styles.chip}>
                                    <CIcon icon={cilTag} size="sm" /> {pet.pet_identification}
                                </span>
                                <span className={styles.chip}>
                                    <CIcon icon={cilPaw} size="sm" /> {[pet.specie, pet.breed].filter(Boolean).join(" · ")}
                                </span>
                                {!pet.is_alive && (
                                    <span className={`${styles.chip} ${styles.chipDead}`}>
                                        <CIcon icon={cilHeart} size="sm" /> Recordado
                                    </span>
                                )}
                            </div>
                        </div>
                        <CButton
                            className="btn-custom-yellow"
                            href={`${FICHAS_URL}${pet.pet_identification}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <CIcon icon={cilExternalLink} className="me-2" />
                            Ver ficha pública
                        </CButton>
                    </div>

                    <Section icon={cilInfo} title="Datos de la mascota">
                        {pet.images?.length > 0 && (
                            <div className={styles.gallery}>
                                {pet.images.map((img) => (
                                    <img
                                        key={img.id}
                                        src={imageUrl(img)}
                                        alt={pet.name}
                                        className={`${styles.galleryImg} ${styles.zoomable}`}
                                        onClick={() => setViewerImage(img)}
                                    />
                                ))}
                            </div>
                        )}
                        <CRow>
                            <Field icon={cilTag} label="ID">{pet.id}</Field>
                            <Field icon={cilPaw} label="Especie">{pet.specie}</Field>
                            <Field icon={cilPaw} label="Raza">{pet.breed}</Field>
                            <Field icon={cilUser} label="Género">{pet.gender}</Field>
                            <Field icon={cilResizeBoth} label="Tamaño">{pet.size}</Field>
                            <Field icon={cilCalendar} label="Edad">{pet.age}</Field>
                            <Field icon={cilCalendar} label="Fecha de nacimiento">{pet.date_of_birth}</Field>
                            <Field icon={cilColorPalette} label="Tipo de pelo">{pet.fur_type}</Field>
                            <Field icon={cilColorPalette} label="Tipo de manto">{pet.coat_type}</Field>
                            <Field icon={cilColorPalette} label="Colores">
                                {pet.colors?.map((c) => c.name + (c.is_dominant ? " (dominante)" : "")).join(", ")}
                            </Field>
                            <Field icon={cilMedicalCross} label="Castrado/a">{yesNo(pet.is_castrated)}</Field>
                            <Field icon={cilStar} label="Pedigree">{yesNo(pet.is_pedigree)}</Field>
                            <Field icon={cilHeart} label="Rescatado/a">{yesNo(pet.is_rescued)}</Field>
                            <Field icon={cilHeart} label="En match">{yesNo(pet.in_match)}</Field>
                            <Field icon={cilCalendar} label="Registrada">{pet.created_at}</Field>
                            <Field icon={cilLocationPin} label="Dirección">
                                {pet.address?.address || pet.address?.street}
                            </Field>
                            <Field icon={cilNotes} label="Comentarios">{pet.comments}</Field>
                            <Field icon={cilMedicalCross} label="Tratamientos">{pet.treatments}</Field>
                        </CRow>
                    </Section>

                    <Section icon={cilPeople} title="Dueños">
                        <CTable responsive className={styles.table}>
                            <CTableHead>
                                <CTableRow>
                                    <CTableHeaderCell>ID</CTableHeaderCell>
                                    <CTableHeaderCell>Nombre</CTableHeaderCell>
                                    <CTableHeaderCell>
                                        <CIcon icon={cilEnvelopeClosed} size="sm" className="me-1" />Email
                                    </CTableHeaderCell>
                                    <CTableHeaderCell>
                                        <CIcon icon={cilPhone} size="sm" className="me-1" />Teléfono
                                    </CTableHeaderCell>
                                    <CTableHeaderCell>Tipo</CTableHeaderCell>
                                </CTableRow>
                            </CTableHead>
                            <CTableBody>
                                {pet.owners?.map((u) => (
                                    <CTableRow key={u.id}>
                                        <CTableDataCell>{u.id}</CTableDataCell>
                                        <CTableDataCell>{u.name}</CTableDataCell>
                                        <CTableDataCell>{u.email}</CTableDataCell>
                                        <CTableDataCell>{u.phone}</CTableDataCell>
                                        <CTableDataCell>
                                            <span className={styles.chip}>
                                                {u.is_permanent_owner ? "Permanente" : "Compartido"}
                                            </span>
                                        </CTableDataCell>
                                    </CTableRow>
                                ))}
                            </CTableBody>
                        </CTable>
                    </Section>

                    {pet.memorial && (
                        <Section icon={cilHeart} title="Recordatorio en redes">
                            <CRow>
                                <Field icon={cilTag} label="Instagram">{pet.memorial.instagram_handle}</Field>
                                <Field icon={cilInfo} label="Estado">{pet.memorial.status}</Field>
                                <Field icon={cilCalendar} label="Solicitado">{pet.memorial.created_at}</Field>
                            </CRow>
                        </Section>
                    )}

                    <Section icon={cilBell} title="Alertas">
                        {pet.alerts?.length ? (
                            <CTable responsive className={styles.table}>
                                <CTableHead>
                                    <CTableRow>
                                        <CTableHeaderCell>ID</CTableHeaderCell>
                                        <CTableHeaderCell>Tipo</CTableHeaderCell>
                                        <CTableHeaderCell>Estado</CTableHeaderCell>
                                        <CTableHeaderCell>Fecha</CTableHeaderCell>
                                        <CTableHeaderCell>Dirección</CTableHeaderCell>
                                    </CTableRow>
                                </CTableHead>
                                <CTableBody>
                                    {pet.alerts.map((a) => (
                                        <CTableRow key={a.id}>
                                            <CTableDataCell>{a.id}</CTableDataCell>
                                            <CTableDataCell>{a.type}</CTableDataCell>
                                            <CTableDataCell>{a.status}</CTableDataCell>
                                            <CTableDataCell>{a.date_of_alert}</CTableDataCell>
                                            <CTableDataCell>{a.address}</CTableDataCell>
                                        </CTableRow>
                                    ))}
                                </CTableBody>
                            </CTable>
                        ) : (
                            <div className={styles.empty}>Sin alertas.</div>
                        )}
                    </Section>
                </CContainer>
            )}
            <CModal
                size="lg"
                alignment="center"
                visible={!!viewerImage}
                onClose={() => setViewerImage(null)}
            >
                <CModalHeader>
                    <CModalTitle>{pet?.name}</CModalTitle>
                </CModalHeader>
                <CModalBody className="text-center">
                    {viewerImage && (
                        <>
                            <img
                                src={imageUrl(viewerImage)}
                                alt={pet?.name}
                                className={styles.viewerImg}
                            />
                            <div className="mt-3">
                                <CButton
                                    className="btn-custom-yellow"
                                    onClick={() => downloadImage(viewerImage)}
                                >
                                    <CIcon icon={cilCloudDownload} className="me-2" />
                                    Descargar
                                </CButton>
                            </div>
                        </>
                    )}
                </CModalBody>
            </CModal>
            {!loading && !pet && <CContainer className="mt-4">No se encontró la mascota.</CContainer>}
        </div>
    );
};

export default Pet;
